import React, { useState, useRef, useEffect } from 'react';
import { Coins } from 'lucide-react';
import { useQuota } from '../../context/QuotaContext';
import {
  shouldShowQuota,
  usagePercent,
  formatTokens,
  usageTotal,
  hasQuotaLimit,
  formatExactTokens,
} from '../../utils/quota';
import QuotaPopup from './QuotaPopup';

const QuotaPill: React.FC = () => {
  const { quota } = useQuota();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!shouldShowQuota(quota)) return null;
  const q = quota!;
  const total = usageTotal(q);
  const scopedLabel = q.usage_scope === 'user' ? 'השימוש שלך החודש' : 'שימוש הארגון החודש';
  const quotaAvailable = hasQuotaLimit(q);
  const pct = usagePercent(q.used, q.limit);
  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`${scopedLabel}: ${formatExactTokens(total)} טוקנים (הערכה)`}
        aria-label={`${scopedLabel}: ${formatExactTokens(total)} טוקנים`}
        aria-expanded={open}
        className="flex min-h-9 items-center gap-1.5 rounded-md border border-sidebar-700/70 px-2 text-xs text-sidebar-200 transition-colors hover:border-sidebar-600 hover:bg-sidebar-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
      >
        <Coins className="h-3.5 w-3.5 text-primary-300" aria-hidden="true" />
        <span className="hidden xl:inline text-sidebar-400">טוקנים</span>
        <span className="tabular-nums font-semibold text-white">{formatTokens(total)}</span>
        {quotaAvailable && (
          <span className="hidden h-1.5 w-9 overflow-hidden rounded-full bg-sidebar-700 sm:block" aria-hidden="true">
            <span className="block h-full bg-primary-400 transition-[width] duration-300 motion-reduce:transition-none" style={{ width: `${pct}%` }} />
          </span>
        )}
      </button>
      {open && <QuotaPopup quota={q} onClose={() => setOpen(false)} />}
    </div>
  );
};

export default QuotaPill;
